// metaDataStore.ts
import { A } from "@adornis/base/env-info";
import { BaseError } from "@adornis/validation/errors/BaseError";
import { MultipleValidationError } from "@adornis/validation/errors/MultipleValidationError";
import { ValidationError } from "@adornis/validation/errors/ValidationError";
import "luxon";
import { getTypeString } from "./baseqlTypes";
import { AdornisEntity } from "./entities/adornisEntity";
import { NotAuthorizedError } from "./errors/NotAuthorizedError";
import { PermissionsNotSetError } from "./errors/PermissionsNotSetError";
var Tree = class {
  constructor(root) {
    this.root = root;
  }
  find(compareFn, node = this.root) {
    if (compareFn(node))
      return node;
    for (const childNode of node.children) {
      const foundNode = this.find(compareFn, childNode);
      if (foundNode)
        return foundNode;
    }
  }
  forEach(fn, node = this.root, depth = 0) {
    fn(node, depth);
    node.children.forEach((child) => this.forEach(fn, child, depth + 1));
  }
  reduce(fn, startVal, node = this.root, depth = 0) {
    let nextVal = fn(startVal, node, depth);
    node.children.forEach((child) => nextVal = this.reduce(fn, nextVal, child, depth + 1));
    return nextVal;
  }
  get size() {
    let size = 0;
    this.forEach((n) => size++);
    return size;
  }
};
var MetaTree = class extends Tree {
  getAllFields(node = this.root, direction = 0, excludeStatic = true) {
    const fields = [...node.fields];
    if (direction <= 0 && node.parent)
      fields.unshift(
        ...this.getAllFields(node.parent, -1).filter(excludeStatic ? withoutStaticFields : () => true).filter((field) => !fields.map((f) => f.name).includes(field.name))
      );
    if (direction >= 0)
      node.children.forEach(
        (child) => fields.push(
          ...this.getAllFields(child, 1).filter(excludeStatic ? withoutStaticFields : () => true).filter((field) => !fields.map((f) => f.name).includes(field.name))
        )
      );
    return fields;
  }
  getAllNames(node, direction = 0) {
    const names = [getTypeString(node.type)];
    if (direction <= 0 && node.parent)
      names.unshift(...this.getAllNames(node.parent, -1));
    if (direction >= 0)
      node.children.forEach((child) => names.push(...this.getAllNames(child, 1)));
    return names;
  }
  resolveNode(input) {
    if (input instanceof AdornisEntity)
      input = input._class;
    if (typeof input === "function") {
      const output = this.find((node) => node.type() === input());
      if (!output)
        throw new Error(`Could not resolve node from ${getTypeString(input)}`);
      return output;
    }
    if (typeof input === "string") {
      const output = this.find((node) => getTypeString(node.type) === input);
      if (!output)
        throw new Error(`Could not resolve node from ${input}`);
      return output;
    }
    return input;
  }
};
var staticFields = [
  { name: "_class", type: () => String, arrayDepth: () => 0, options: {}, params: [], middlewares: [] }
];
var withoutStaticFields = (field) => !staticFields.map((sf) => sf.name).includes(field.name);
var queries = [];
var mutations = [];
var subscriptions = [];
var errors = [];
var allOperationDefinitions = () => queries.concat(mutations).concat(subscriptions);
var getOperationDefinition = (doc) => {
  const opDef = doc.definitions[0];
  if (!opDef || opDef.kind !== "OperationDefinition")
    throw new Error("no OperationDefinition node found");
  const selectedField = opDef.selectionSet.selections[0];
  if (!selectedField || selectedField.kind !== "Field")
    throw new Error("no Field node found in SelectionSet");
  const op = allOperationDefinitions().find((op2) => op2.name === selectedField.name.value);
  if (!op)
    throw new Error(`Could not find BaseQL metadata for operation with name ${selectedField.name.value}
  Did you forget to import an entity containing this operation before server start?`);
  return op;
};
var metaState = { sealed: false };
var metaDataStore = new MetaTree({
  type: () => AdornisEntity,
  fields: [],
  children: [],
  options: {}
});
var noPropertyNameGivenSymbol = Symbol("no property name");
var BQLReflect = class {
  static setMetadata(data, target, propertyKey = noPropertyNameGivenSymbol) {
    if (!this.metaDataMap.has(target))
      this.metaDataMap.set(target, /* @__PURE__ */ new Map());
    this.metaDataMap.get(target).set(propertyKey, data);
  }
  static getMetadata(target, propertyKey = noPropertyNameGivenSymbol) {
    return this.metaDataMap.get(target)?.get(propertyKey);
  }
};
BQLReflect.metaDataMap = /* @__PURE__ */ new WeakMap();
var buildMetaData = (EntityClasses) => {
  let didNotFit = [];
  let round = 0;
  while (EntityClasses.length !== didNotFit.length) {
    if (round) {
      EntityClasses = didNotFit;
      didNotFit = [];
    }
    for (let i = 0; i < EntityClasses.length; i++) {
      const Entity = EntityClasses[i];
      const proto = Object.getPrototypeOf(Entity);
      const protoNode = metaDataStore.find((node) => node.type() === proto);
      if (!protoNode) {
        didNotFit.push(Entity);
        continue;
      }
      let fields = BQLReflect.getMetadata(Entity);
      if (!fields) {
        console.warn(`Entity ${Entity._class} has no field metadata.`);
        fields = [...staticFields];
      }
      protoNode.children.push({
        type: () => Entity,
        parent: protoNode,
        fields,
        options: {},
        children: []
      });
    }
    round++;
    console.log("round", round, EntityClasses, didNotFit);
  }
  metaState.sealed = true;
};
function registerError(errorClass) {
  if (errors.find((op) => op.name === errorClass._class))
    throw new Error(`Trying to register duplicate error ${errorClass._class}.`);
  errors.push(errorClass);
}
registerError(BaseError);
registerError(ValidationError);
registerError(MultipleValidationError);
registerError(NotAuthorizedError);
registerError(PermissionsNotSetError);
if (A.isDevelopment && !A.isServiceWorker) {
  globalThis.metaDataStore = metaDataStore;
}
export {
  BQLReflect,
  allOperationDefinitions,
  buildMetaData,
  errors,
  getOperationDefinition,
  metaDataStore,
  metaState,
  mutations,
  queries,
  registerError,
  staticFields,
  subscriptions,
  withoutStaticFields
};
